import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";

export type MemberOwnedEmails = {
  loginEmail?: string | null;
  contactEmail?: string | null;
};

export const MEMBER_OWNED_EMAIL_VALIDATION_MESSAGE =
  "You can't use your own email address";

function normalizeEmailForComparison(email: string | null | undefined): string {
  if (typeof email !== "string") {
    return "";
  }

  return email.replace(/\s/g, "").toLowerCase();
}

export function isMemberOwnedEmail(
  candidateEmail: string,
  memberEmails: MemberOwnedEmails
): boolean {
  const normalizedCandidate = normalizeEmailForComparison(candidateEmail);
  if (!normalizedCandidate) {
    return false;
  }

  const ownedEmails = new Set<string>();
  const loginEmail = normalizeEmailForComparison(memberEmails.loginEmail);
  const contactEmail = normalizeEmailForComparison(memberEmails.contactEmail);

  if (loginEmail) {
    ownedEmails.add(loginEmail);
  }
  if (contactEmail) {
    ownedEmails.add(contactEmail);
  }

  return ownedEmails.has(normalizedCandidate);
}

function validateNotMemberOwnedEmail(
  candidateEmail: string,
  memberEmails: MemberOwnedEmails
): string | undefined {
  return isMemberOwnedEmail(candidateEmail, memberEmails)
    ? MEMBER_OWNED_EMAIL_VALIDATION_MESSAGE
    : undefined;
}

export function useMemberOwnedEmailValidation() {
  const { user } = useAuth0();
  const contactEmail = useSelector(selectUserPersonalInformation).email;

  const memberEmails = useMemo<MemberOwnedEmails>(
    () => ({
      loginEmail: user?.email,
      contactEmail
    }),
    [contactEmail, user?.email]
  );

  const validateRecipientEmail = useCallback(
    (normalizedEmail = ""): string | undefined =>
      validateNotMemberOwnedEmail(normalizedEmail, memberEmails),
    [memberEmails]
  );

  const isRecipientEmailOwnedByMember = useCallback(
    (normalizedEmail = ""): boolean =>
      isMemberOwnedEmail(normalizedEmail, memberEmails),
    [memberEmails]
  );

  return {
    isRecipientEmailOwnedByMember,
    memberEmails,
    validateRecipientEmail
  };
}
